/**
 * KeyboardMouseController — desktop input source implementing `ShipInputSource`.
 *
 * Bindings:
 *   W / S            throttle target up / down
 *   ArrowUp/Down     pitch
 *   ArrowLeft/Right  roll
 *   A / D            yaw
 *   Space            thrust (hold)
 *   F                ping (edge-triggered)
 *   M                toggle map (edge-triggered)
 *
 * Edge-triggered actions (`ping`, `toggleMap`) are debounced to one true
 * sample per key press: the next `sample()` returns false even if the key
 * is still down, matching the contract described in `shared/src/input.ts`.
 *
 * Routing of user actions through the codebase:
 *
 *   keydown / keyup events
 *     -> this.keys[key] = true/false
 *     -> sample() -> ShipInput
 *        -> game/LocalSim.ts and net/NetClient.ts
 */

import {
	type ShipInput,
	type ShipInputSource,
	clampAxis,
	clampUnit,
	zeroShipInput,
} from "@squid/shared";

const THROTTLE_PER_SECOND = 0.6;

export class KeyboardMouseController implements ShipInputSource {
	private readonly keys = new Set<string>();
	private throttle = 0;
	private pingPending = false;
	private mapPending = false;
	private lastSampleMs: number | null = null;
	private readonly removeListeners: () => void;
	private readonly out: ShipInput = zeroShipInput();
	private readonly now: () => number;

	constructor(opts: { now?: () => number; target?: EventTarget } = {}) {
		this.now = opts.now ?? (() => performance.now());
		const target: EventTarget =
			opts.target ?? (typeof window !== "undefined" ? window : new EventTarget());

		const onKeyDown = (e: Event): void => {
			const ke = e as KeyboardEvent;
			const key = ke.key.toLowerCase();
			if (!this.keys.has(key)) {
				if (key === "f") this.pingPending = true;
				if (key === "m") this.mapPending = true;
			}
			this.keys.add(key);
		};
		const onKeyUp = (e: Event): void => {
			const ke = e as KeyboardEvent;
			this.keys.delete(ke.key.toLowerCase());
		};
		const onBlur = (): void => this.keys.clear();
		target.addEventListener("keydown", onKeyDown);
		target.addEventListener("keyup", onKeyUp);
		target.addEventListener("blur", onBlur);
		this.removeListeners = () => {
			target.removeEventListener("keydown", onKeyDown);
			target.removeEventListener("keyup", onKeyUp);
			target.removeEventListener("blur", onBlur);
		};
	}

	sample(): ShipInput {
		const now = this.now();
		const dt = this.lastSampleMs === null ? 0 : (now - this.lastSampleMs) / 1000;
		this.lastSampleMs = now;

		if (this.keys.has("w")) this.throttle = clampUnit(this.throttle + THROTTLE_PER_SECOND * dt);
		if (this.keys.has("s")) this.throttle = clampUnit(this.throttle - THROTTLE_PER_SECOND * dt);

		const pitch = (this.keys.has("arrowdown") ? 1 : 0) + (this.keys.has("arrowup") ? -1 : 0);
		const roll = (this.keys.has("arrowright") ? 1 : 0) + (this.keys.has("arrowleft") ? -1 : 0);
		const yaw = (this.keys.has("d") ? 1 : 0) + (this.keys.has("a") ? -1 : 0);

		this.out.throttle = this.throttle;
		this.out.pitch = clampAxis(pitch);
		this.out.yaw = clampAxis(yaw);
		this.out.roll = clampAxis(roll);
		this.out.thrust = this.keys.has(" ");
		this.out.ping = this.pingPending;
		this.out.toggleMap = this.mapPending;
		this.pingPending = false;
		this.mapPending = false;
		return this.out;
	}

	dispose(): void {
		this.removeListeners();
		this.keys.clear();
	}
}
